import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignUp } from '@clerk/clerk-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, CheckCircle, Check } from 'lucide-react';
import { AnimateIn, Button, GoogleIcon } from '../../shared/ui';
import { useRecordConsent } from '../../features/users/hooks';
import { PRIVACY_POLICY_VERSION } from '../../shared/constants/business';
import { PENDING_CONSENT_KEY } from './consent-storage';
import { getClerkErrorMessage, matchClerkStatus } from '../../lib/clerkFlow';
import { AuthField } from './components/AuthField';
import { AuthPasswordField } from './components/AuthPasswordField';
import { PasswordStrengthMeter } from './components/PasswordStrengthMeter';
import { AuthOtpField } from './components/AuthOtpField';
import { AuthServerError } from './components/AuthServerError';
import { AuthCaptcha } from './components/AuthCaptcha';
import { AuthSubmitButton } from './components/AuthSubmitButton';
import { usePageMeta } from '../../shared/hooks/usePageMeta';

const signupSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  agreedToTerms: z.boolean().refine((v) => v === true, {
    message: 'You must agree to the Terms and Privacy Policy to continue.',
  }),
});

const otpSchema = z.object({
  code: z.string().length(6, 'Enter the 6-digit code'),
});

type SignupData = z.infer<typeof signupSchema>;
type OtpData = z.infer<typeof otpSchema>;

export function SignupPage() {
  usePageMeta('Sign Up');
  const { signUp, setActive, isLoaded } = useSignUp();
  const [step, setStep] = useState<'register' | 'verify'>('register');
  const [serverError, setServerError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const recordConsent = useRecordConsent();

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const signupForm = useForm<SignupData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { agreedToTerms: false },
  });
  const otpForm = useForm<OtpData>({ resolver: zodResolver(otpSchema) });
  const agreedToTerms = signupForm.watch('agreedToTerms');

  const signUpWithGoogle = async () => {
    if (!isLoaded || !signUp) return;
    if (!signupForm.getValues('agreedToTerms')) {
      await signupForm.trigger('agreedToTerms');
      return;
    }
    sessionStorage.setItem(PENDING_CONSENT_KEY, PRIVACY_POLICY_VERSION);
    setGoogleLoading(true);
    try {
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: '/app/requests',
      });
    } catch {
      sessionStorage.removeItem(PENDING_CONSENT_KEY);
      setGoogleLoading(false);
    }
  };

  const completeSignIn = async (sessionId: string) => {
    if (!setActive) return;
    await setActive({ session: sessionId });
    try {
      await recordConsent.mutateAsync(PRIVACY_POLICY_VERSION);
    } catch {
      // Best-effort — don't block the user from reaching the portal over a consent-log failure.
    }
    navigate('/app/requests', { replace: true });
  };

  const onSignup = async ({ name, email, password }: SignupData) => {
    if (!isLoaded || !signUp) return;
    setServerError('');
    try {
      // Clerk still models identity as firstName/lastName under the hood — split the single
      // name/company-name input on the first space so a lone word (e.g. a one-word company
      // name) doesn't send an empty lastName. Our own backend just rejoins these with a space
      // to reconstruct the original string (see rtBackend's upsertFromClerk), so this split is
      // lossless for anything this codebase actually displays.
      const [firstName, ...rest] = name.trim().split(/\s+/);
      const lastName = rest.join(' ') || undefined;

      // This Clerk instance requires express legal consent (`legal_accepted`) as its own
      // tracked field, separate from our own agreedToTerms UI gate above — without passing it
      // here, the sign-up would stay stuck at `missing_requirements` forever even after a
      // correct email verification code, since Clerk never considers it "complete".
      const result = await signUp.create({
        firstName,
        lastName,
        emailAddress: email,
        password,
        legalAccepted: true,
      });
      await matchClerkStatus(result, {
        missing_requirements: async () => {
          await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
          setStep('verify');
        },
        // Edge case if this Clerk instance ever has email verification disabled: the sign-up
        // is already done, so go straight to sign-in instead of trying to prepare a
        // verification step that doesn't exist.
        complete: async (r) => {
          await completeSignIn(r.createdSessionId!);
        },
        fallback: () => {
          setServerError('Could not create account. Please try again.');
        },
      });
    } catch (err: unknown) {
      setServerError(getClerkErrorMessage(err, 'Could not create account.'));
    }
  };

  const RESEND_COOLDOWN_SECONDS = 30;

  const resendCode = async () => {
    if (!isLoaded || !signUp || resending || resendCooldown > 0) return;
    setResending(true);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      // best-effort; the user can just try entering a code if one already arrived
    } finally {
      setResending(false);
    }
  };

  const onVerify = async ({ code }: OtpData) => {
    if (!isLoaded || !signUp) return;
    setServerError('');
    try {
      const result = await signUp.attemptEmailAddressVerification({ code });
      await matchClerkStatus(result, {
        complete: async (r) => {
          await completeSignIn(r.createdSessionId!);
        },
        // `missing_requirements` can mean the code itself was wrong (email still in
        // unverifiedFields) OR that the code worked but this Clerk instance requires something
        // else this form never collects (e.g. phone number) — these need different messages,
        // since the second case isn't fixable by re-entering a code.
        missing_requirements: (r) => {
          if (r.unverifiedFields?.includes('email_address')) {
            setServerError("That code didn't work. Please try again.");
            return;
          }
          const missing = r.missingFields?.join(', ');
          setServerError(
            missing
              ? `Your email is verified, but this account is still missing: ${missing}. Contact support to finish setting it up.`
              : 'Could not complete sign-up. Please contact support.',
          );
        },
        fallback: () => {
          setServerError("That code didn't work. Please try again.");
        },
      });
    } catch (err: unknown) {
      // A prior attempt (e.g. a duplicate submit) can complete verification server-side even
      // though THIS call rejects with "already verified" — recover by reloading from the server
      // rather than trusting the stale local snapshot. Reload once and inspect the full resource
      // (not just recoverCompletedSession's session-id-or-null) so an "already verified but still
      // missing something else" state is also surfaced instead of shown as a generic code error.
      const reloaded = await signUp.reload().catch(() => null);
      if (reloaded?.status === 'complete' && reloaded.createdSessionId) {
        await completeSignIn(reloaded.createdSessionId);
        return;
      }
      if (reloaded && !reloaded.unverifiedFields?.includes('email_address') && reloaded.missingFields?.length) {
        setServerError(
          `Your email is verified, but this account is still missing: ${reloaded.missingFields.join(', ')}. Contact support to finish setting it up.`,
        );
        return;
      }
      setServerError(getClerkErrorMessage(err, 'Invalid code.'));
    }
  };

  if (step === 'verify') {
    return (
      <div className="w-full step-enter">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 bg-accent rounded-2xl flex items-center justify-center flex-shrink-0">
            <CheckCircle size={20} className="text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white leading-tight">Check your email.</h1>
            <p className="text-white/35 text-xs mt-0.5">We sent a 6-digit code to your inbox</p>
          </div>
        </div>

        <form onSubmit={otpForm.handleSubmit(onVerify)} className="flex flex-col gap-5">
          <Controller
            control={otpForm.control}
            name="code"
            defaultValue=""
            render={({ field }) => (
              <AuthOtpField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                onComplete={() => otpForm.handleSubmit(onVerify)()}
                error={otpForm.formState.errors.code?.message}
              />
            )}
          />

          <AuthServerError message={serverError} />

          <AuthSubmitButton
            isLoaded={!!isLoaded}
            isSubmitting={otpForm.formState.isSubmitting}
            loadingText="Verifying..."
          >
            Verify & continue <ArrowRight size={15} />
          </AuthSubmitButton>

          <Button
            type="button"
            variant="ghost"
            onClick={resendCode}
            disabled={resending || resendCooldown > 0}
            className="!text-accent !text-xs normal-case !tracking-normal !px-0 hover:!text-accent/70 hover:!bg-transparent self-start disabled:!opacity-50"
          >
            {resending ? 'Sending…' : resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() => setStep('register')}
            className="!text-white/55 !text-xs normal-case !tracking-normal !px-0 hover:!text-white/80 hover:!bg-transparent"
          >
            ← Back
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full step-enter">
      <AnimateIn animation="fade-up">
        <h1 className="mb-1.5 text-center text-[clamp(32px,8vw,40px)] font-bold leading-[1.1] tracking-[-0.035em] text-white">Create your account.</h1>
        <p className="mb-8 text-center text-[#a1a1a6]">
          Already have one?{' '}
          <Link to="/login" className="text-accent hover:underline">
            Sign in
          </Link>
        </p>
      </AnimateIn>

      <AnimateIn animation="fade-up" delay={100}>
        {/* Google */}
        <Button
          type="button"
          variant="social"
          onClick={signUpWithGoogle}
          disabled={!isLoaded}
          loading={googleLoading}
          loadingText="Redirecting..."
          className="mb-5 w-full !rounded-full !border-2 !border-[#424245] !bg-[#f5f5f7] !px-5 !py-[15px] !text-[17px] !font-semibold hover:!bg-white disabled:!opacity-60"
        >
          <GoogleIcon />
          Continue with Google
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="h-0.5 flex-1 bg-[#333336]" />
          <span className="text-[13px] text-[#6e6e73]">or</span>
          <div className="h-0.5 flex-1 bg-[#333336]" />
        </div>
      </AnimateIn>

      <AnimateIn animation="fade-up" delay={200}>
        <form onSubmit={signupForm.handleSubmit(onSignup)} className="flex flex-col gap-4">
          <AuthField
            label="Name or company name"
            placeholder="Devon Campbell or Campbell Logistics"
            autoComplete="name"
            error={signupForm.formState.errors.name?.message}
            {...signupForm.register('name')}
          />

          <AuthField
            label="Email"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            error={signupForm.formState.errors.email?.message}
            {...signupForm.register('email')}
          />

          <AuthPasswordField
            label="Password"
            placeholder="Min. 8 characters"
            autoComplete="new-password"
            error={signupForm.formState.errors.password?.message}
            {...signupForm.register('password')}
          />
          <PasswordStrengthMeter password={signupForm.watch('password') ?? ''} />

          <AuthServerError message={serverError} />

          <AuthCaptcha />

          <div>
            <div
              role="checkbox"
              aria-checked={agreedToTerms}
              aria-invalid={!!signupForm.formState.errors.agreedToTerms}
              tabIndex={0}
              onClick={() => signupForm.setValue('agreedToTerms', !agreedToTerms, { shouldValidate: true })}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                signupForm.setValue('agreedToTerms', !agreedToTerms, { shouldValidate: true });
              }}
              className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 cursor-pointer select-none transition-colors ${
                signupForm.formState.errors.agreedToTerms ? 'bg-red-400/10' : agreedToTerms ? 'bg-accent/10' : 'bg-white/5 hover:bg-white/[0.08]'
              }`}
            >
              <span
                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
                  agreedToTerms ? 'bg-accent border-accent' : 'border-white/20'
                }`}
              >
                {agreedToTerms && <Check size={12} className="text-black" strokeWidth={3} />}
              </span>
              <span className="text-sm text-white/70 leading-relaxed">
                I agree to the{' '}
                <Link to="/terms" onClick={(e) => e.stopPropagation()} className="text-accent font-semibold hover:underline">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" onClick={(e) => e.stopPropagation()} className="text-accent font-semibold hover:underline">Privacy Policy</Link>.
              </span>
            </div>
            {signupForm.formState.errors.agreedToTerms && (
              <p className="text-red-400 text-xs mt-2">
                {signupForm.formState.errors.agreedToTerms.message}
              </p>
            )}
          </div>

          <AuthSubmitButton
            isLoaded={!!isLoaded}
            isSubmitting={signupForm.formState.isSubmitting}
            loadingText="Creating account..."
            className="mt-1"
          >
            Create account <ArrowRight size={15} />
          </AuthSubmitButton>
        </form>
      </AnimateIn>
    </div>
  );
}
